import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Star, Pin } from "lucide-react";

const DEFAULTS = { accent: "#FF4F00", theme: "light", layout: "masonry" };

export default function Widget() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/campaigns/${slug}/wall`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <div className="p-6 text-sm text-zinc-400">Mural não encontrado.</div>;
  if (!data) return null;

  const s = { ...DEFAULTS, ...(data.settings || {}) };
  const dark = s.theme === "dark";
  const cardCls = dark
    ? "bg-zinc-900 border border-zinc-800 text-zinc-100"
    : "bg-white border border-zinc-200 text-zinc-900";
  const mutedCls = dark ? "text-zinc-400" : "text-zinc-500";
  const bodyCls = dark ? "text-zinc-300" : "text-zinc-700";
  const emptyStarCls = dark ? "text-zinc-700" : "text-zinc-200";

  if (data.testimonials.length === 0) {
    return <div className={`p-6 text-sm ${mutedCls}`} data-testid="widget-empty">Ainda não há depoimentos publicados.</div>;
  }

  const layoutWrap =
    s.layout === "grid"
      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      : s.layout === "list"
      ? "max-w-xl space-y-4"
      : "masonry-cols";
  const itemCls = s.layout === "masonry" ? "masonry-item" : "";

  const Card = ({ t }) => (
    <div
      data-testid={`widget-card-${t.id}`}
      className={`${itemCls} ${cardCls} rounded-lg p-5`}
      style={t.pinned ? { borderColor: s.accent, borderWidth: 2 } : undefined}
    >
      {t.pinned && (
        <div className="flex items-center gap-1.5 mb-3 text-xs font-semibold" style={{ color: s.accent }} data-testid={`pinned-badge-${t.id}`}>
          <Pin size={12} className="fill-current" /> Destaque
        </div>
      )}
      {t.highlight && (
        <p data-testid={`widget-highlight-${t.id}`} className="font-heading font-bold text-lg tracking-tight leading-snug mb-3">
          {t.highlight}
        </p>
      )}
      {t.type === "video" && (
        <video
          src={`${API}/videos/${t.id}`}
          poster={t.has_poster ? `${API}/videos/${t.id}/poster` : undefined}
          controls
          playsInline
          preload={t.has_poster ? "none" : "metadata"}
          className="w-full rounded-md bg-black mb-4"
        />
      )}
      {t.type === "audio" && (
        <audio src={`${API}/videos/${t.id}`} controls preload="metadata" className="w-full mb-4" />
      )}
      <div className="flex gap-0.5 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            size={14}
            className={n <= t.rating ? "fill-current" : emptyStarCls}
            style={n <= t.rating ? { color: s.accent } : undefined}
          />
        ))}
      </div>
      {t.type === "text" && <p className={`text-sm ${bodyCls} leading-relaxed mb-4`}>"{t.text}"</p>}
      {(t.type === "video" || t.type === "audio") && t.transcript && (
        <p data-testid={`widget-transcript-${t.id}`} className={`text-sm ${bodyCls} leading-relaxed mb-4`}>"{t.transcript}"</p>
      )}
      <div className="flex items-center gap-3">
        {t.has_avatar ? (
          <img
            src={`${API}/testimonials/${t.id}/avatar`}
            alt={t.author_name}
            className="w-9 h-9 rounded-full object-cover shrink-0"
            style={{ border: `2px solid ${s.accent}` }}
          />
        ) : (
          <div
            className="w-9 h-9 rounded-full text-white flex items-center justify-center text-sm font-bold shrink-0"
            style={{ backgroundColor: s.accent }}
          >
            {t.author_name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{t.author_name}</p>
          {t.author_role && <p className={`text-xs ${mutedCls} truncate`}>{t.author_role}</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4" style={{ backgroundColor: dark ? "#0A0A0A" : "transparent", minHeight: "100vh" }} data-testid="widget-wall">
      {data.count > 0 && (
        <div
          data-testid="widget-rating-seal"
          className={`inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full border ${dark ? "bg-zinc-900 border-zinc-800" : "bg-white border-zinc-200"}`}
        >
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={13}
                className={n <= Math.round(data.avg_rating) ? "fill-current" : emptyStarCls}
                style={n <= Math.round(data.avg_rating) ? { color: s.accent } : undefined}
              />
            ))}
          </div>
          <span className={`font-bold text-sm ${dark ? "text-zinc-100" : "text-zinc-900"}`} data-testid="widget-avg-rating">
            {data.avg_rating.toFixed(1).replace(".", ",")}
          </span>
          <span className={`text-xs ${mutedCls}`} data-testid="widget-rating-count">
            · {data.count} avaliaç{data.count === 1 ? "ão" : "ões"}
          </span>
        </div>
      )}
      <div className={layoutWrap}>
        {data.testimonials.map((t) => (
          <Card key={t.id} t={t} />
        ))}
      </div>
    </div>
  );
}
